import { useMutation } from "@apollo/client";
import {
  ChevronDownIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  ExclamationIcon,
  InformationCircleIcon,
  UserGroupIcon,
  UsersIcon,
} from "@heroicons/react/outline";
import { SwitchHorizontalIcon } from "@heroicons/react/outline";
import { PlusIcon } from "@heroicons/react/solid";
import { withErrorBoundary } from "@sentry/react";
import { compact, uniqueId } from "lodash";
import uniqBy from "lodash/uniqBy";
import moment from "moment";
import { useContext } from "react";
import { CgArrowsExpandRight } from "react-icons/cg";
import { useLocation } from "react-router-dom";
import {
  FinalizePermissions,
  MeetingParticipantFragmentFragment,
  UserStatus,
} from "types/graphql-schema";
import { MeetingViewMeetingNodeNewPageFragmentFragment } from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import MeetingDropdownMenu from "@apps/meeting-dropdown-menu/meeting-dropdown-menu";
import MeetingContentAccessDropdown from "@apps/meeting-settings/components/content-access-dropdown";
import ProfileCoachingInsights from "@apps/profile/overview-tab/components/profile-coaching-insights";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Avatars from "@components/avatar/avatars";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import DraftLabel from "@components/draft-label/draft-label";
import Error from "@components/error/error";
import AppLink from "@components/link/link";
import AppPopover from "@components/popover/app-popover";
import ProgramIcon from "@components/program-icon/program-icon";
import {
  ToggleButtonGroup,
  ToggleButtonGroupTheme,
  ToggleButtonGroupType,
} from "@components/toggle-button-group/toggle-button-group";
import InfoTooltip from "@components/tooltip/info-tooltip";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import {
  delay,
  ignoredParticipantEmails,
  tempCacheIdPrefix,
  topicState,
} from "@helpers/constants";
import { classNames } from "@helpers/css";
import {
  assertEdgesNonNull,
  assertEdgesNonNullWithStringId,
  formatTime,
  getUrl,
  getUrlUsingTypename,
  removeCurrentYear,
} from "@helpers/helpers";
import { pluralize, titleCase } from "@helpers/string";

import createOrUpdateTopicMutation from "../graphql/create-or-update-topic-mutation";
import getMeetingQuery from "../graphql/get-meeting-query";
import TopicNodeFragment from "../graphql/topic-node-fragment";
import updateMeetingGroupMutation from "../graphql/update-meeting-group-mutation";
import { getMeetingAllowVisibilityLabel } from "../helpers";
import { getFinalizePermissionLabel } from "./details-helpers";
import { MeetingViewEnum } from "./header-constants";
import { MeetingPageContext } from "./meeting-page-context";
import { MeetingVideoConferenceImage } from "./meeting/video-conference-button";
import { getMeetingVideoConferenceName } from "./meeting/video-conference-helpers";
import MeetingNotetakerCheckbox from "./notetaker-checkbox";
import PublishMeetingButton from "./publish-meeting-button";
import ConvertToReview1On1Banner from "./review/convert-to-review-1-on-1-banner";

const filterInactiveParticipant = (
  participant: MeetingParticipantFragmentFragment,
) => {
  return (
    participant.user?.status === UserStatus.Inactive &&
    participant.participantEmail &&
    !window.TF.topicflowBotEmails.includes(participant.participantEmail)
  );
};

const MeetingDetails = ({
  meeting,
  onShowShareNotesDialog,
  onChangeMeetingView,
  meetingView,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  onShowShareNotesDialog: () => void;
  meetingView: MeetingViewEnum;
  onChangeMeetingView: (option: ToggleButtonGroupType<MeetingViewEnum>) => void;
}) => {
  const label = useLabel();
  const location = useLocation<TFLocationState>();
  const currentUser = currentUserVar();
  const { isInSidebar, isInExtension } = useContext(MeetingPageContext);

  const participants = assertEdgesNonNullWithStringId(meeting.participants);
  const inactiveUserEmails = participants
    .filter(filterInactiveParticipant)
    .map((node) => node.participantEmail);
  const avatars = compact(
    participants.map((node) =>
      node.user
        ? {
            ...node.user,
            disabled: node.status === "declined",
            tooltipSuffix: node.status === "declined" ? " (declined)" : "",
          }
        : null,
    ),
  );

  const participantUsers = compact(participants.map(({ user }) => user));
  const facilitator = meeting?.meetingGroup?.facilitator || participantUsers[0];
  const subject = participantUsers.find(
    ({ id, email }) =>
      id !== facilitator?.id && !ignoredParticipantEmails.includes(email),
  );
  const labelClassName = "w-28 font-medium text-gray-500";
  const contentClassName =
    "flex-1 flex gap-4 items-center flex-wrap gap-y-1 min-h-[28px]";
  const subContentClassName = "flex gap-1.5 items-center";

  const finalizePermission =
    meeting.meetingGroup?.topicTemplate?.finalizePermissions ||
    FinalizePermissions.FacilitatorsAndAdmins;

  const [updateMeetingGroup, { loading: loadingSwitch }] = useMutation(
    updateMeetingGroupMutation,
    {
      refetchQueries: [getMeetingQuery],
    },
  );

  const handleClickSwitch = () => {
    if (!subject) return;
    if (meeting?.meetingGroup?.isFormalOneonone) {
      updateMeetingGroup({
        variables: {
          meetingGroupId: meeting.meetingGroup.id,
          facilitatorId: subject.id,
        },

        optimisticResponse: {
          updateMeetingGroup: {
            meetingGroup: {
              ...meeting.meetingGroup,
              facilitator: subject,
            },
          },
        },
      });
    }
  };

  const [createTopic] = useMutation(createOrUpdateTopicMutation);

  const handleClickAddTopic = () => {
    const tempId = uniqueId(`${tempCacheIdPrefix}-topic`);
    const isFormalOneonone = !!meeting.meetingGroup?.isFormalOneonone;
    const topicData = {
      id: tempId,
      title: "New Topic",
      description: "",
      includesIndividualNotes: isFormalOneonone,
      includesIndividualNotesForCurrentUser: isFormalOneonone,
      includesSharedNotes: !isFormalOneonone,
      discussionNotes: null,
      isMandatory: false,
      canUpdate: { __typename: "PermissionNode", permission: true },
      canDelete: { __typename: "PermissionNode", permission: true },
      created: moment().format(),
      state: topicState.open,
      eventChannelName: `topic-${tempId}`,
      creator: currentUser,
      assignee: currentUser,
      previousTopic: null,
      linkedTemplateTopic: null,
      notesRequirement: null,
      comments: { totalCount: 0, __typename: "CommentNodeConnection" },
      individualNotes: {
        edges: [],
        __typename: "IndividualNoteNodeConnection",
      },
      copiedFrom: null,
      __typename: "TopicNode",
    };
    createTopic({
      variables: {
        meetingId: meeting.id,
        meetingGroupId: meeting.meetingGroup?.id,
        title: "New Topic",
        includesIndividualNotes: isFormalOneonone,
        includesSharedNotes: !isFormalOneonone,
      },
      optimisticResponse: {
        createOrUpdateTopic: {
          topic: topicData,
          __typename: "CreateOrUpdateTopicMutation",
        },
      },
      onCompleted: ({ createOrUpdateTopic }) => {
        setTimeout(() => {
          const container = document.querySelector(
            `#meeting-${meeting.id}-topic-${createOrUpdateTopic.topic.id}`,
          );
          if (!container) return;
          // If user is already editing with custom text, don't override
          const editInput = container.querySelector(
            '[aria-label="Edit topic title"]',
          ) as HTMLInputElement | null;
          if (editInput && editInput.value && editInput.value !== "New Topic") {
            return;
          }
          const titleEl = container.querySelector(
            '[aria-label="Topic title"]',
          ) as HTMLElement | null;
          titleEl?.click();
        }, delay.waitTillFocus);
      },
      update(cache, response) {
        const { topic } = response.data.createOrUpdateTopic;
        const createdTopicCacheNode = cache.writeFragment({
          data: topic,
          fragment: TopicNodeFragment,
          fragmentName: "TopicNodeNewPageFragment",
          variables: { meetingGroupId: meeting.meetingGroup?.id },
        });
        const meetingCacheId = cache.identify(meeting);
        cache.modify({
          id: meetingCacheId,
          fields: {
            topics(cachedTopics) {
              const mergedEdges = [
                ...cachedTopics.edges,
                {
                  node: createdTopicCacheNode,
                  __typename: "TopicNodeEdge",
                },
              ];
              const uniqueEdges = uniqBy(mergedEdges, ({ node }) => node.__ref);
              return { edges: uniqueEdges };
            },
          },
        });
      },
    });
  };

  const assessmentDeliveriesMatching1on1Template =
    meeting?.assessmentDeliveriesMatching1on1Template
      ? assertEdgesNonNull(meeting.assessmentDeliveriesMatching1on1Template)
      : [];
  const assessmentDeliveryMatching1on1Template =
    assessmentDeliveriesMatching1on1Template[0];
  const topicTemplateOfAssessmentDelivery =
    assessmentDeliveryMatching1on1Template?.complianceProgram
      ?.requiredTopicTemplates.edges[0]?.node;
  const meetingGroup = meeting.meetingGroup;

  if (!meetingGroup) return null;

  return (
    <>
      <div
        className={classNames(
          "border-b empty:hidden flex flex-col pb-6 gap-2 text-sm text-gray-900",
          "hidden",
        )}
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="">
            <AppLink
              target={isInExtension ? "_blank" : undefined}
              to={getUrl({
                meetingGroupId: meeting.meetingGroup.id,
                meetingId: meeting.id,
              })}
              className="hover:underline text-2xl font-semibold align-middle tracking-tight"
            >
              {meeting.title}
            </AppLink>
            {meeting.draft && (
              <div className="align-middle ml-3 inline-flex items-center">
                <DraftLabel className="mt-1" />
              </div>
            )}
          </div>
          <div className="mt-1 flex items-center gap-2 empty:hidden shrink-0">
            {!isInSidebar && !isInExtension && meeting.draft && (
              <PublishMeetingButton meeting={meeting}>
                Publish
                <span className="hidden @2xl/layout-main:inline"> Meeting</span>
              </PublishMeetingButton>
            )}
            {isInSidebar && (
              <Button
                theme={buttonTheme.iconGray}
                className="shrink-0"
                icon={CgArrowsExpandRight}
                to={getUrl({
                  meetingId: meeting.id,
                  meetingGroupId: meetingGroup.id,
                })}
              />
            )}

            {!isInExtension && !!meeting.videoConferenceUrl && (
              <Button
                className={classNames("gap-2 shrink-0")}
                tooltip={`Join ${getMeetingVideoConferenceName({
                  meeting,
                  compact: false,
                })}`}
                url={meeting.videoConferenceUrl}
                target="_blank"
              >
                <MeetingVideoConferenceImage
                  meeting={meeting}
                  className="h-5 w-5"
                />
                {!isInSidebar && (
                  <span className="hidden @lg/layout-main:inline shrink-0">
                    <span className="hidden @2xl/layout-main:inline shrink-0">
                      Join{" "}
                    </span>
                    {getMeetingVideoConferenceName({
                      meeting,
                      compact: false,
                    })}
                  </span>
                )}
              </Button>
            )}

            {isInSidebar && (
              <MeetingDropdownMenu
                meeting={meeting}
                meetingGroup={meetingGroup}
                size={5}
              />
            )}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <div className={labelClassName}>Details</div>
          <div className={contentClassName}>
            <div className={subContentClassName}>
              <TopicflowIcon type="meeting" className="h-4 w-4 text-gray-400" />
              {meeting.startDatetime && meeting.endDatetime ? (
                <Tooltip
                  text={`${moment(meeting.startDatetime).format(
                    "LL",
                  )}, ${formatTime(
                    moment(meeting.startDatetime).format("h:mma"),
                  )}-${formatTime(
                    moment(meeting.endDatetime).format("h:mma"),
                  )}`}
                >
                  <span className={subContentClassName}>
                    {removeCurrentYear(
                      moment(meeting.startDatetime).format("ll"),
                    )}
                    ,{" "}
                    {formatTime(moment(meeting.startDatetime).format("h:mma"))}
                    {!isInExtension &&
                      `-${formatTime(
                        moment(meeting.endDatetime).format("h:mma"),
                      )}`}
                  </span>
                </Tooltip>
              ) : (
                <span className={subContentClassName}>Unscheduled</span>
              )}
            </div>
            <div className={subContentClassName}>
              {meeting.meetingGroup.isFormalOneonone ? (
                <UsersIcon className="h-4 w-4 text-gray-400" />
              ) : (
                <UserGroupIcon className="h-4 w-4 text-gray-400" />
              )}
              {meeting.meetingGroup.isFormalOneonone
                ? label("1-on-1", { capitalize: true })
                : "Meeting"}
              {meeting.meetingGroup.isFormalOneonone && (
                <InfoTooltip
                  text={`${getFinalizePermissionLabel(
                    finalizePermission,
                  )} Once finalized, the ${label(
                    "1-on-1",
                  )} won't be editable anymore.`}
                />
              )}
            </div>
          </div>
        </div>
        {/* Show only when there is a template and it's not the same as the assessment delivery template, otherwise it can create confusion. */}
        {meeting.meetingGroup?.topicTemplate &&
          !topicTemplateOfAssessmentDelivery &&
          meeting.meetingGroup.topicTemplate.publicTemplate && (
            <div className="flex gap-2 items-center">
              <div className={labelClassName}>Template</div>
              <div className={contentClassName}>
                <AppLink
                  className={classNames(subContentClassName, "hover:underline")}
                  to={`${getUrl({
                    meetingGroupId: meeting.meetingGroup?.id,
                  })}/settings`}
                >
                  <DocumentDuplicateIcon className="h-4 w-4 text-gray-400" />
                  {meeting.meetingGroup?.topicTemplate.title}
                </AppLink>
              </div>
            </div>
          )}
        {!meeting.meetingGroup.isFormalOneonone && (
          <div className="flex gap-2 items-center">
            <div className={labelClassName}>Participants</div>
            <div className={contentClassName}>
              <Avatars
                users={avatars}
                size={5}
                max={10}
                modalTitle="Participants"
              />
              {!isInExtension && inactiveUserEmails.length > 0 && (
                <Tooltip
                  text={`${inactiveUserEmails.length} ${pluralize(
                    "guest",
                    inactiveUserEmails.length,
                  )} in this meeting ${pluralize(
                    "is",
                    inactiveUserEmails.length,
                  )}n't ${
                    inactiveUserEmails.length === 1 ? "a" : ""
                  } Topicflow  ${pluralize("user", inactiveUserEmails.length)}`}
                >
                  <button
                    aria-label="Invite non registered users to meeting button"
                    className={classNames(
                      "hidden sm:flex items-center gap-0.5 text-sm text-yellow-600 rounded-md hover:bg-yellow-100 px-1 py-0.5",
                    )}
                    onClick={onShowShareNotesDialog}
                  >
                    <ExclamationIcon className="h-4 w-4" />
                    {inactiveUserEmails.length}
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-2 items-center">
          <div className={labelClassName}>Visibility</div>
          <div className={classNames(contentClassName, "flex-1 min-w-0")}>
            <MeetingContentAccessDropdown meetingGroup={meetingGroup}>
              <AppPopover.Button
                as={Button}
                theme={buttonTheme.text}
                leftNegativeMargin
                aria-label="Meeting permission dropdown menu button"
                disabled={!meetingGroup?.canUpdate?.permission}
                className="min-w-0" // necessary for truncating text if too long
              >
                <Tooltip
                  text={titleCase(
                    getMeetingAllowVisibilityLabel({
                      allowManagementTreeVisibility:
                        meetingGroup.allowManagementTreeVisibility,
                      allowAdminVisibility: meetingGroup.allowAdminVisibility,
                      allowOrgVisibility: meetingGroup.allowOrgVisibility,
                    }),
                  )}
                >
                  <span className="flex items-center gap-1 min-w-0 flex-1">
                    {meetingGroup.allowAdminVisibility ||
                    meetingGroup.allowOrgVisibility ? (
                      <TopicflowIcon
                        type="lock"
                        className="h-4 w-4 text-gray-400 shrink-0"
                      />
                    ) : (
                      <TopicflowIcon
                        type="lockClosed"
                        className="h-4 w-4 text-gray-400 shrink-0"
                      />
                    )}
                    <span className="truncate flex-1 min-w-0">
                      {titleCase(
                        getMeetingAllowVisibilityLabel(
                          {
                            allowManagementTreeVisibility:
                              meetingGroup.allowManagementTreeVisibility,
                            allowAdminVisibility:
                              meetingGroup.allowAdminVisibility,
                            allowOrgVisibility: meetingGroup.allowOrgVisibility,
                          },
                          true,
                        ),
                      )}
                    </span>
                    {meetingGroup?.canUpdate?.permission && (
                      <ChevronDownIcon className="h-3 w-3 text-gray-400 shrink-0" />
                    )}
                  </span>
                </Tooltip>
              </AppPopover.Button>
            </MeetingContentAccessDropdown>
          </div>
        </div>

        {assessmentDeliveryMatching1on1Template && (
          <>
            {assessmentDeliveryMatching1on1Template.complianceProgram && (
              <div className="flex gap-2 items-center">
                <div className={labelClassName}>Program</div>
                <div className={contentClassName}>
                  <div className={subContentClassName}>
                    <Button
                      url={`/programs/${assessmentDeliveryMatching1on1Template.complianceProgram.id}`}
                      theme={buttonTheme.text}
                      leftNegativeMargin
                    >
                      <ProgramIcon className="h-4 w-4 text-gray-400" />
                      {`Review: ${assessmentDeliveryMatching1on1Template.complianceProgram.title}`}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <div className="flex gap-2 items-center">
              <div className={labelClassName}>
                {label("review", { capitalize: true })}
              </div>
              <div className={contentClassName}>
                <div className={subContentClassName}>
                  <Button
                    to={getUrlUsingTypename(
                      assessmentDeliveryMatching1on1Template,
                      location,
                    )}
                    theme={buttonTheme.text}
                    leftNegativeMargin
                  >
                    <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                    {`Review: ${assessmentDeliveryMatching1on1Template.target?.name}`}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}

        {meeting.meetingGroup.isFormalOneonone && (
          <div className="flex gap-2 items-center">
            <div className={labelClassName}>Facilitator</div>
            <div className={contentClassName}>
              <div className={subContentClassName}>
                <Avatar user={facilitator} size={4} />
                {facilitator.name}
                {meeting.canUpdate?.permission && !meeting.isFinalized && (
                  <Tooltip text="Switch facilitator & subject">
                    <button
                      className={classNames(
                        "text-gray-300 p-1 rounded-md",
                        loadingSwitch &&
                          "hover:text-gray-800 hover:bg-gray-100",
                      )}
                      onClick={handleClickSwitch}
                      disabled={loadingSwitch}
                    >
                      <SwitchHorizontalIcon className="h-4 w-4" />
                    </button>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        )}
        {meeting.meetingGroup.isFormalOneonone && (
          <div className="flex gap-2 items-center">
            <div className={labelClassName}>Subject</div>
            <div className={contentClassName}>
              <div className={subContentClassName}>
                <Avatar user={subject} size={4} />
                {subject?.name}
              </div>
            </div>
          </div>
        )}

        <div
          className="flex gap-2 items-center target:bg-yellow-100"
          id="highlight-meeting-notetaker"
        >
          <div className={labelClassName}>Notetaker</div>
          <div className={contentClassName}>
            <MeetingNotetakerCheckbox
              meeting={meeting}
              className={subContentClassName}
            />
          </div>
        </div>

        {meeting.hasExternalParticipants && (
          <div className="flex gap-2 items-center bg-amber-100 text-amber-700 px-3 py-2 rounded-md">
            <InformationCircleIcon className="h-4 w-4 text-amber-700" />
            External participants can see this meeting's notes and summary.
          </div>
        )}

        {meeting.meetingGroup.isFormalOneonone &&
          meeting.canUpdate?.permission &&
          meeting.assessmentDeliveriesMatching1on1Template.edges.length ===
            0 && <ConvertToReview1On1Banner meeting={meeting} />}

        {waffle.flag_is_active("coaching-insights") &&
          meeting.meetingGroup.isFormalOneonone &&
          subject && (
            <div className="">
              <ProfileCoachingInsights profile={subject} />
            </div>
          )}
      </div>

      <div className="flex flex-col gap-4 pb-2 ">
        <div className="@container/toggle-button-group">
          <div className="flex gap-4 items-center justify-between">
            <div
              className={classNames(
                "flex items-center text-sm text-gray-600",
                meeting.canUpdate?.permission ? "gap-2" : "gap-4",
              )}
            >
              <ToggleButtonGroup<MeetingViewEnum>
                theme={ToggleButtonGroupTheme.separatedWhiteButtons}
                buttons={compact([
                  {
                    value: MeetingViewEnum.notes,
                    title: MeetingViewEnum.notes,
                    active: MeetingViewEnum.notes === meetingView,
                  },
                  {
                    value: MeetingViewEnum.summary,
                    title: MeetingViewEnum.summary,
                    active: MeetingViewEnum.summary === meetingView,
                  },
                ])}
                onClick={onChangeMeetingView}
              />
            </div>

            <div className="flex items-center gap-3">
              {!isInExtension && !!meeting.videoConferenceUrl && (
                <Button
                  size={ButtonSize.medium}
                  className="gap-2"
                  tooltip={`Join ${getMeetingVideoConferenceName({
                    meeting,
                    compact: false,
                  })}`}
                  url={meeting.videoConferenceUrl}
                  target="_blank"
                >
                  <MeetingVideoConferenceImage
                    meeting={meeting}
                    className="h-4 w-4"
                  />
                  {!isInSidebar && (
                    <span className="hidden @lg/layout-main:inline shrink-0">
                      <span className="hidden @2xl/layout-main:inline shrink-0">
                        Join{" "}
                      </span>
                      {getMeetingVideoConferenceName({
                        meeting,
                        compact: false,
                      })}
                    </span>
                  )}
                </Button>
              )}
              {meeting.canUpdate?.permission && !meeting.isFinalized && (
                <Button
                  size={ButtonSize.medium}
                  className="gap-1"
                  onClick={handleClickAddTopic}
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Topic
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const MeetingDetailsWithSentry = withErrorBoundary(MeetingDetails, {
  fallback: <Error description={"The meeting could not be rendered."} />,
});

export default MeetingDetailsWithSentry;
